/**
 * useSkuDetail — MS61c lazy hydration for the ProductDetailModal.
 *
 * The SkuCardData shape that flows through chat / browse / vendor
 * surfaces is intentionally lean (just enough for a card render:
 * name, vendor, price, image, highlight chips). When the buyer
 * opens the ProductDetailModal, we need the FULL vendor_products
 * row including the MS61b enrichment columns (gallery_image_urls,
 * long_description, voucher_terms, contents_list, partner_venues,
 * included_inclusions, policy_text, availability_windows,
 * parent_product_id, variant_label).
 *
 * Rather than pollute SkuCardData with detail-only fields that
 * every card render would carry, we fetch on demand when the modal
 * opens. Single Postgres round-trip; cached by react-query under
 * the sku id; stale-while-revalidate so reopening the same SKU is
 * instant.
 *
 * Variant lookup (sister-SKU collapse, MS61b schema):
 *   - When the row has parent_product_id set → fetch the parent +
 *     all siblings (rows whose parent_product_id = this row's
 *     parent_product_id).
 *   - When the row has parent_product_id NULL → fetch any rows
 *     whose parent_product_id = this row's id (children of this
 *     parent).
 *
 * The "variants" array returned includes the parent + all variants
 * sorted by price asc. The modal's variant picker shows this list
 * when it has length > 1; the SKU initially shown in the modal is
 * the one the buyer clicked.
 *
 * No variant data exists yet (the MS61c data migration that sets
 * parent_product_id on existing sister SKUs is a separate PR).
 * Until that lands, the variants array will always be length 1
 * and the picker stays hidden — but the wiring is in place.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/** Full vendor_products row shape — includes the MS61b enrichment
 *  columns. Mirrors what `vendor_products.*` returns; we narrow to
 *  the fields the ProductDetailModal actually reads. */
export interface SkuDetail {
  id: string;
  name: string;
  description: string | null;
  long_description: string | null;
  voucher_terms: string | null;
  price: number;
  image_url: string | null;
  gallery_image_urls: string[];
  slug: string | null;
  vendor_category: string | null;
  is_voucher: boolean;
  contents_list: ContentsListItem[];
  partner_venues: PartnerVenue[];
  included_inclusions: string[];
  policy_text: string | null;
  availability_windows: AvailabilityWindow[];
  parent_product_id: string | null;
  variant_label: string | null;
  vendor_name: string;
  vendor_partner_id: string;
  dietary_tags: string[];
  lead_time_days: number | null;
  serves_min: number | null;
  serves_max: number | null;
}

export interface ContentsListItem {
  label: string;
  quantity?: number;
  note?: string;
  allergen_flags?: string[];
}

export interface PartnerVenue {
  name: string;
  address?: string;
  suburb?: string;
  hours?: string;
  url?: string;
}

export interface AvailabilityWindow {
  start_at: string;
  end_at: string;
  capacity: number;
  capacity_remaining?: number;
  sku_variant?: string;
}

export interface SkuDetailResult {
  /** The SKU the buyer clicked (the row we opened the modal with). */
  sku: SkuDetail | null;
  /** The full variant set (parent + all siblings). Length 1 when the
   *  SKU is standalone. Sorted by price asc. Always includes `sku`. */
  variants: SkuDetail[];
}

/** PostgREST select string. The two embedded relationships fetch
 *  vendor_partner_id → vendor_partners(vendor_name) and the
 *  per-row join data. Kept as one string constant so the
 *  hydration query for variants reuses the same shape. */
const FULL_SELECT = `
  id, name, description, long_description, voucher_terms, price,
  image_url, gallery_image_urls, slug, vendor_category, is_voucher,
  contents_list, partner_venues, included_inclusions, policy_text,
  availability_windows, parent_product_id, variant_label,
  vendor_partner_id, dietary_tags, lead_time_days, serves_min,
  serves_max,
  vendor_partners(vendor_name)
` as const;

// Narrow PostgREST's loose row shape into the SkuDetail interface.
// vendor_partners can come back as `{ vendor_name }` or `null` or
// (in PostgREST's older array-form) `[{ vendor_name }]`; the
// guards handle all three so we never crash on a malformed row.
function rowToSkuDetail(row: Record<string, unknown>): SkuDetail {
  const partner = row.vendor_partners as
    | { vendor_name?: string }
    | Array<{ vendor_name?: string }>
    | null;
  const partnerName = Array.isArray(partner)
    ? partner[0]?.vendor_name ?? "Lembra"
    : partner?.vendor_name ?? "Lembra";

  return {
    id: String(row.id),
    name: String(row.name ?? "Untitled"),
    description: (row.description as string | null) ?? null,
    long_description: (row.long_description as string | null) ?? null,
    voucher_terms: (row.voucher_terms as string | null) ?? null,
    price: Number(row.price ?? 0),
    image_url: (row.image_url as string | null) ?? null,
    gallery_image_urls: Array.isArray(row.gallery_image_urls)
      ? (row.gallery_image_urls as string[])
      : [],
    slug: (row.slug as string | null) ?? null,
    vendor_category: (row.vendor_category as string | null) ?? null,
    is_voucher: Boolean(row.is_voucher),
    contents_list: Array.isArray(row.contents_list)
      ? (row.contents_list as ContentsListItem[])
      : [],
    partner_venues: Array.isArray(row.partner_venues)
      ? (row.partner_venues as PartnerVenue[])
      : [],
    included_inclusions: Array.isArray(row.included_inclusions)
      ? (row.included_inclusions as string[])
      : [],
    policy_text: (row.policy_text as string | null) ?? null,
    availability_windows: Array.isArray(row.availability_windows)
      ? (row.availability_windows as AvailabilityWindow[])
      : [],
    parent_product_id: (row.parent_product_id as string | null) ?? null,
    variant_label: (row.variant_label as string | null) ?? null,
    vendor_name: partnerName,
    vendor_partner_id: String(row.vendor_partner_id),
    dietary_tags: Array.isArray(row.dietary_tags)
      ? (row.dietary_tags as string[])
      : [],
    lead_time_days: (row.lead_time_days as number | null) ?? null,
    serves_min: (row.serves_min as number | null) ?? null,
    serves_max: (row.serves_max as number | null) ?? null,
  };
}

async function fetchSkuDetail(skuId: string): Promise<SkuDetailResult> {
  // Step 1: fetch the row the buyer clicked.
  const { data: primary, error: primaryError } = await supabase
    .from("vendor_products")
    .select(FULL_SELECT)
    .eq("id", skuId)
    .maybeSingle();

  if (primaryError) throw new Error(primaryError.message);
  if (!primary) return { sku: null, variants: [] };

  const primaryDetail = rowToSkuDetail(primary as Record<string, unknown>);

  // Step 2: variant set. Two cases:
  //   • This row has a parent → fetch all rows whose
  //     parent_product_id = primary.parent_product_id, plus the
  //     parent itself.
  //   • This row has no parent → fetch all rows whose
  //     parent_product_id = primary.id (its children). The primary
  //     is the parent of its own variant set.
  let variants: SkuDetail[] = [primaryDetail];

  if (primaryDetail.parent_product_id) {
    // Case A: variant — fetch parent + siblings
    const [parentResult, siblingsResult] = await Promise.all([
      supabase
        .from("vendor_products")
        .select(FULL_SELECT)
        .eq("id", primaryDetail.parent_product_id)
        .maybeSingle(),
      supabase
        .from("vendor_products")
        .select(FULL_SELECT)
        .eq("parent_product_id", primaryDetail.parent_product_id)
        .neq("id", primaryDetail.id),
    ]);

    const parentRow = parentResult.data
      ? rowToSkuDetail(parentResult.data as Record<string, unknown>)
      : null;
    const siblingRows = Array.isArray(siblingsResult.data)
      ? siblingsResult.data.map((r) => rowToSkuDetail(r as Record<string, unknown>))
      : [];

    variants = [
      ...(parentRow ? [parentRow] : []),
      primaryDetail,
      ...siblingRows,
    ].sort((a, b) => a.price - b.price);
  } else {
    // Case B: parent — fetch children
    const { data: childrenData } = await supabase
      .from("vendor_products")
      .select(FULL_SELECT)
      .eq("parent_product_id", primaryDetail.id);

    if (Array.isArray(childrenData) && childrenData.length > 0) {
      const childDetails = childrenData.map((r) =>
        rowToSkuDetail(r as Record<string, unknown>),
      );
      variants = [primaryDetail, ...childDetails].sort(
        (a, b) => a.price - b.price,
      );
    }
  }

  return { sku: primaryDetail, variants };
}

/** Hook used by ProductDetailModal. Lazy-fetches the full SKU row +
 *  variant set when the modal opens. Returns react-query state so
 *  the modal can render a loading skeleton on first open and stale
 *  data on subsequent re-opens (cache hit = instant).
 *
 *  `enabled` defaults to true; pass false when you want to gate the
 *  fetch on some external state (rare). */
export function useSkuDetail(skuId: string | null, enabled = true) {
  return useQuery<SkuDetailResult>({
    queryKey: ["sku-detail", skuId],
    queryFn: () => {
      if (!skuId) throw new Error("skuId is required");
      return fetchSkuDetail(skuId);
    },
    enabled: enabled && !!skuId,
    // Detail data updates rarely (vendors don't change voucher terms
    // hourly). 5 minutes stale, 30 minutes garbage-collect — covers
    // a typical browsing session without a server round-trip per
    // re-open.
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}
